import * as React from 'react';
import { IRootState } from 'src/reducers';
import actions from 'src/actions';
import selectors from 'src/selectors';
import { connect } from 'react-redux';
import { Table, ITableConfig } from '../../common/table';
import PagedResultSet from '../../../models/common/PagedResultSet';
import Rate from '../../../models/Rate';
import { RouteComponentProps } from 'react-router-dom';
import CardContainer from '../../common/CardContainer';
import { Card, Dialog, Button, Intent } from '@blueprintjs/core';
import { Bar } from 'react-chartjs-2';
import Landman from '../../../models/Landman';
import { Form } from 'react-final-form';
import FormTextbox from '../../controls/forms/FormTextbox';
import MathUtil from 'src/util/MathUtil';

export type IManageRatesPageStateProps = {
    isLoading: boolean;
    tableConfig: ITableConfig;
    rates: PagedResultSet<Rate> | undefined;
    isAddRateDialogShowing: boolean;
    landman: (id: number) => Landman;
    newRate: Rate | undefined;
};

export interface IManageRatesPageDispatchProps {
    getLandmanRates: (landmanId: number) => void;
    getLandman: (id: number) => void;
    setIsAddRateDialogShowing: () => void;
    hideDialog: () => void;
    addLandmanRate: (rate: Rate) => void;
}

type IManagePageProps = RouteComponentProps<{ id?: string }> & IManageRatesPageStateProps & IManageRatesPageDispatchProps;

class ManageRatesPage extends React.Component<IManagePageProps> {
    componentDidMount() {
        const landmanId = parseInt(this.props.match.params.id, 10);
        this.props.getLandmanRates(landmanId);
        this.props.getLandman(landmanId);
    }

    hideDialog = () => {
        this.props.hideDialog();
    };

    showDialog = () => {
        this.props.setIsAddRateDialogShowing();
    };

    handleSubmit = (rate: Rate) => {
        rate.userId = parseInt(this.props.match.params.id, 10);
        this.props.addLandmanRate(rate);
    };

    render() {
        const data = {
            labels: ['Highest Rate', 'Avg Rate', 'Lowest Rate', 'Rate for Current Landman'],
            datasets: [
                {
                    label: 'Landman Rate Comparison',
                    backgroundColor: 'rgba(124,156,237.2)',
                    borderColor: 'rgba(124,156,237,1)',
                    borderWidth: 1,
                    hoverBackgroundColor: 'rgba(124,156,237,0.4)',
                    hoverBorderColor: 'rgba(124,156,237,1)',
                    data: [20, 16.667, 14, 16, 0, 30],
                },
            ],
        };

        const landman = this.props.landman(parseInt(this.props.match.params.id, 10));

        return (
            <>
                <CardContainer>
                    <Card elevation={1}>
                        <div className={'card-header lh-mb-small ' + (this.props.isLoading ? 'pt-skeleton' : '')}>
                            Manage Landman Rates: {landman && `${landman.firstName} ${landman.lastName} `}{' '}
                        </div>
                        <div>
                            Effective Rate: Bill - ${landman && landman.effectiveRate ? MathUtil.cleanNumber(landman.effectiveRate.billRate, 5) : 'NA'}, Pay - $
                            {landman && landman.effectiveRate ? MathUtil.cleanNumber(landman.effectiveRate.payRate, 5) : 'NA'}
                        </div>
                    </Card>
                </CardContainer>
                <CardContainer>
                    <Card elevation={1}>
                        <Table
                            key="landman-manage-rates-table"
                            isLoading={this.props.isLoading}
                            tableConfig={this.props.tableConfig}
                            data={this.props.rates || undefined}
                            enablePaging={true}
                        />
                        <button className="bp3-button pt-intent-primary" type="submit" style={{ marginTop: '10px' }} onClick={this.showDialog}>
                            Add New Rate
                        </button>
                    </Card>
                    <Card>
                        <Bar data={data} height={200} options={{ maintainAspectRatio: false }} />
                    </Card>
                </CardContainer>

                <Dialog title="Add New Rate" isOpen={this.props.isAddRateDialogShowing} onClose={this.hideDialog}>
                    <Form
                        onSubmit={this.handleSubmit}
                        render={({ handleSubmit, pristine, invalid }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <div style={{ marginLeft: '20%', marginRight: '20%', marginTop: '3%' }}>
                                        <FormTextbox label="Enter New Pay Rate" name="payRate" placeholder="0.00" />
                                        <FormTextbox label="Enter New Bill Rate" name="billRate" placeholder="0.00" />
                                        <FormTextbox label="Enter New Effective Date" name="effectiveDate" placeholder="MM/DD/YYYY" />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            className="bp3-button"
                                            icon="add"
                                            type="submit"
                                            style={{ marginRight: '10px' }}
                                            intent={Intent.SUCCESS}
                                            disabled={pristine || invalid}
                                        >
                                            Add
                                        </Button>
                                        <Button className="bp3-button" icon="delete" type="button" intent={Intent.DANGER} onClick={this.hideDialog}>
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    />
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IRootState) => ({
    isLoading: selectors.landman.isRatesLoading(state),
    rates: selectors.landman.rates(state),
    tableConfig: selectors.landman.landmanRatePageTableConfig(state),
    isAddRateDialogShowing: selectors.layout.isDialogShowing(state, 'addRate'),
    landman: (id: number) => selectors.landman.landman(id)(state),
});

const mapDispatchToProps: IManageRatesPageDispatchProps = {
    getLandmanRates: (landmanId: number) => actions.landman.getLandmanRates(landmanId),
    setIsAddRateDialogShowing: () => actions.layout.showDialog('addRate'),
    hideDialog: () => actions.layout.hideDialog(),
    getLandman: (id: number) => actions.landman.getLandman(id),
    addLandmanRate: (rate: Rate) => actions.landman.addLandmanRate(rate),
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageRatesPage);



// WEBPACK FOOTER //
// ./src/components/personnel/pages/ManageRatesPage.tsx